import { useQuery } from "@tanstack/react-query";
import { apiV2 } from "api/firebase";
import { RequestKey } from "api/request-key";

const STALE_TIME = 5 * 60_000;
const CACHE_TIME = 10 * 60_000;

/**
 * Cached fetch of ALL stripe-connect accounts for an organiser company.
 *
 * Shared by the layout connect-bar gate (`useShowStripeConnectBar`) and
 * `StripeConnectBar/OrganiserBar`. Both used to fire their OWN raw
 * `apiV2.oldStripe.getOrganiserCompanyAllStripeConnectAccount` in a
 * `useEffect` with unstable deps (`shows` / `permissionPackages` arrays),
 * so the endpoint was hit on nearly every navigation — twice when the bar
 * was visible. react-query dedupes by key + caches across navigation, so
 * the data (which changes very rarely) is fetched ~once per `staleTime`
 * window for the whole session.
 */
export const useOrganiserStripeConnectAccounts = (
  organiserCompanyId: string | undefined,
  enabled = true
) => {
  const { data, isFetched } = useQuery({
    queryKey: [
      RequestKey.GetOrganiserStripeConnectAccounts,
      organiserCompanyId,
    ],
    queryFn: () =>
      apiV2.oldStripe.getOrganiserCompanyAllStripeConnectAccount({
        organiserCompanyId: organiserCompanyId as string,
      }),
    enabled: !!organiserCompanyId && enabled,
    staleTime: STALE_TIME,
    cacheTime: CACHE_TIME,
    refetchOnWindowFocus: false,
  });

  return { accounts: data ?? [], isFetched };
};

/**
 * Cached fetch of a supplier company's stripe-connect account (single,
 * may be null). Same rationale as the organiser variant above.
 */
export const useSupplierStripeConnectAccount = (
  supplierCompanyId: string | undefined,
  enabled = true
) => {
  const { data, isFetched } = useQuery({
    queryKey: [RequestKey.GetSupplierStripeConnectAccount, supplierCompanyId],
    queryFn: () =>
      apiV2.oldStripe.getSupplierCompanyStripeConnectAccount({
        supplierCompanyId: supplierCompanyId as string,
      }),
    enabled: !!supplierCompanyId && enabled,
    staleTime: STALE_TIME,
    cacheTime: CACHE_TIME,
    refetchOnWindowFocus: false,
  });

  return { account: data ?? null, isFetched };
};
